/**
 * Slides a visual band's image against the page scroll — the "optie parallax" of the
 * Visual block (Figma 681:22564). Plain rAF-throttled scroll listener with an off-screen
 * bail — IntersectionObserver proved unreliable in preview contexts, and this is cheap
 * enough without it.
 *
 * The element is always present; it does nothing unless the band asked for the scroll
 * link, i.e. unless the media carries `data-visual-media`. It also opts out entirely
 * under prefers-reduced-motion, where the CSS pins the media flush and the band is just
 * a cropped image.
 */

/*
 * Guarded as a whole, not just the `customElements.define`.
 *
 * Two sections on one page can each emit this <script>, and the browser executes a classic
 * script once per tag: the second run re-declares the top-level `class` binding and throws
 * "Identifier 'FilliersVisual' has already been declared". Loading twice is normal here rather than a
 * mistake to design out — `page.verhaal.json` legitimately carries two sections that both need
 * this file — so the file survives it instead. Guarding only the define left the class
 * declaration exposed, which is what actually threw.
 */
if (!customElements.get('filliers-visual')) {
  class FilliersVisual extends HTMLElement {
    connectedCallback() {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      this.media = this.querySelector('[data-visual-media]');
      if (!this.media) return;

      this.travel = parseFloat(this.dataset.travel) || 120;
      this.frame = null;
      this.onScroll = this.onScroll.bind(this);

      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });
      this.update();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
      if (this.frame) cancelAnimationFrame(this.frame);
    }

    onScroll() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.update();
      });
    }

    update() {
      const rect = this.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top > window.innerHeight) return;

      // -1 as the band leaves through the top, +1 as it enters from the bottom.
      const centre = rect.top + rect.height / 2;
      const progress = (window.innerHeight / 2 - centre) / ((window.innerHeight + rect.height) / 2);
      this.media.style.transform = `translate3d(0, ${(progress * this.travel).toFixed(1)}px, 0)`;
    }
  }

  customElements.define('filliers-visual', FilliersVisual);
}
